/**
 * Patched version of @radix-ui/react-compose-refs that is compatible with
 * React 19's ref-callback cleanup semantics.
 *
 * In React 19, when a ref callback changes between renders, React:
 *   1. Calls the OLD callback's cleanup function (if it returned one), OR
 *      calls the old callback with `null` if no cleanup was returned.
 *   2. Calls the NEW callback with the DOM node.
 *
 * The upstream compose-refs (1.1.2) only returns a cleanup function when at
 * least one individual ref returns a cleanup.  For refs that call `setState`
 * (e.g. `(node) => setButton(node)` inside Radix Switch), `setState` returns
 * `undefined`, so no cleanup is produced.  React then falls back to calling
 * the old composed-ref with `null`, which triggers `setState(null)` → re-render
 * → new composed-ref → React sees ref changed → cleanup(null) again → infinite loop.
 *
 * The fix: ALWAYS return a cleanup function so React never falls back to the
 * "call old ref with null" path.
 */
import * as React from 'react';

type PossibleRef<T> = React.Ref<T> | undefined;

function setRef<T>(ref: PossibleRef<T>, value: T) {
  if (typeof ref === 'function') {
    return ref(value);
  } else if (ref !== null && ref !== undefined) {
    (ref as React.RefObject<T>).current = value;
  }
}

function composeRefs<T>(...refs: PossibleRef<T>[]) {
  return (node: T) => {
    const cleanups = refs.map((ref) => setRef(ref, node));

    return () => {
      for (let i = 0; i < cleanups.length; i++) {
        const cleanup = cleanups[i];
        if (typeof cleanup === 'function') {
          cleanup();
        } else {
          setRef(refs[i], null as unknown as T);
        }
      }
    };
  };
}

function useComposedRefs<T>(...refs: PossibleRef<T>[]) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useCallback(composeRefs(...refs), refs);
}

export { composeRefs, useComposedRefs };
